import { useState, useEffect, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const loreLines = [
  "The sewers stir beneath the chain...",
  "Ancient protocols awaken...",
  "Gas fees dissolve into the void...",
  "The gremlins remember everything...",
  "Validators whisper in the dark...",
  "The mempool bleeds its secrets...",
  "Something watches from below...",
  "The ritual nears completion...",
];

const GLYPHS = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X', '0x', '$', 'TX', 'GAS'];

const LoadingScreen = ({ onLoadComplete }) => {
  const [progress, setProgress] = useState(0);
  const [msgIdx, setMsgIdx] = useState(0);
  const [isExiting, setIsExiting] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [activeEvent, setActiveEvent] = useState(null);
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const logoUrl = 'https://customer-assets.emergentagent.com/job_8a97e9c9-a75b-4894-af5b-5ab6ea8e8ae3/artifacts/d97xwjyk_Logo.png';
  const API = process.env.REACT_APP_BACKEND_URL || '';

  const handleEnter = useCallback(() => {
    setIsExiting(true);
    setTimeout(onLoadComplete, 900);
  }, [onLoadComplete]);

  useEffect(() => {
    fetch(`${API}/api/market/cap`)
      .then(r => r.json())
      .then(data => {
        if (data.market_cap && !data.is_mock) {
          const mc = data.market_cap;
          const events = [
            [5000000, { name: 'Aether Chancellor', color: '#dc2626' }],
            [1000000, { name: 'The Storm', color: '#8b5cf6' }],
            [500000, { name: 'The Flood', color: '#06b6d4' }],
            [250000, { name: 'The Hunt Begins', color: '#f59e0b' }],
            [100000, { name: 'Chaos Unleashed', color: '#ef4444' }],
            [50000, { name: 'The Awakening', color: '#eab308' }],
            [25000, { name: 'The Gathering', color: '#a855f7' }],
            [10000, { name: 'The First Drip', color: '#22c55e' }],
          ];
          for (const [threshold, evt] of events) {
            if (mc >= threshold) { setActiveEvent(evt); break; }
          }
        }
      })
      .catch(() => {});
  }, [API]);

  useEffect(() => {
    const progTimer = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) { clearInterval(progTimer); setIsReady(true); return 100; }
        return prev + Math.random() * 10 + 3;
      });
    }, 220);
    const msgTimer = setInterval(() => setMsgIdx(prev => (prev + 1) % loreLines.length), 1100);
    return () => { clearInterval(progTimer); clearInterval(msgTimer); };
  }, []);

  // Orbiting glyphs (skip on mobile for perf)
  const orbitGlyphs = useMemo(() => isMobile ? [] : GLYPHS.map((g, i) => ({
    glyph: g,
    angle: (i / GLYPHS.length) * 360,
    radius: 220 + (i % 3) * 40,
    duration: 30 + i * 2,
    size: i % 4 === 0 ? 14 : 10,
    color: i % 3 === 0 ? 'rgba(168,85,247,0.2)' : i % 3 === 1 ? 'rgba(0,255,136,0.15)' : 'rgba(255,140,0,0.12)',
    glow: i % 3 === 0 ? 'rgba(168,85,247,0.4)' : i % 3 === 1 ? 'rgba(0,255,136,0.3)' : 'rgba(255,140,0,0.25)',
  })), [isMobile]);

  // Rising embers data (fewer on mobile)
  const embers = useMemo(() => [...Array(isMobile ? 10 : 30)].map((_, i) => ({
    id: i,
    left: `${Math.random() * 100}%`,
    w: `${1.5 + Math.random() * 3}px`,
    h: `${1.5 + Math.random() * 3}px`,
    bg: i % 3 === 0 ? '#a855f7' : i % 3 === 1 ? '#00ff88' : '#ff8c00',
    shadow: `0 0 ${4 + Math.random() * 4}px ${i % 3 === 0 ? 'rgba(168,85,247,0.6)' : i % 3 === 1 ? 'rgba(0,255,136,0.5)' : 'rgba(255,140,0,0.5)'}`,
    yEnd: -(800 + Math.random() * 300),
    xDrift: (Math.random() - 0.5) * 80,
    dur: 4 + Math.random() * 4,
    delay: Math.random() * 5,
  })), [isMobile]);

  return (
    <AnimatePresence>
      {!isExiting && (
        <motion.div
          initial={{ opacity: 1 }}
          exit={{ opacity: 0, scale: 1.08, filter: 'blur(8px)' }}
          transition={{ duration: 0.9, ease: 'easeInOut' }}
          data-testid="loading-screen"
          className="fixed inset-0 z-[100] flex items-center justify-center overflow-hidden"
          style={{ background: '#020004' }}
        >

          {/* ═══ CATHEDRAL BACKGROUND IMAGE (skip on mobile for perf) ═══ */}
          {!isMobile && (
            <motion.div
              className="absolute inset-0 pointer-events-none"
              initial={{ opacity: 0, scale: 1.1 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 3, ease: 'easeOut' }}
            >
              <img
                src="/images/loader_cathedral.png"
                alt=""
                className="w-full h-full object-cover"
                style={{ opacity: 0.25, filter: 'saturate(1.2) brightness(0.7)' }}
              />
              {/* Dark overlay to keep UI readable */}
              <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at 50% 40%, rgba(2,0,4,0.5) 0%, rgba(2,0,4,0.75) 50%, rgba(2,0,4,0.92) 100%)' }} />
            </motion.div>
          )}

          {/* ═══ DEEP BACKGROUND LAYER: Nebula fog ═══ */}
          <div className="absolute inset-0 pointer-events-none">
            {/* Purple nebula */}
            <motion.div
              className="absolute"
              style={{
                width: '120%', height: '120%', top: '-10%', left: '-10%',
                background: 'radial-gradient(ellipse at 30% 60%, rgba(88,28,135,0.12) 0%, transparent 50%), radial-gradient(ellipse at 70% 30%, rgba(168,85,247,0.08) 0%, transparent 45%)',
              }}
              animate={{ rotate: [0, 3, -2, 0], scale: [1, 1.02, 0.98, 1] }}
              transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
            />
            {/* Green mist */}
            <motion.div
              className="absolute"
              style={{
                width: '100%', height: '100%',
                background: 'radial-gradient(ellipse at 60% 80%, rgba(0,255,136,0.04) 0%, transparent 40%), radial-gradient(ellipse at 20% 20%, rgba(0,255,136,0.03) 0%, transparent 35%)',
              }}
              animate={{ x: [0, 30, -20, 0], y: [0, -15, 10, 0] }}
              transition={{ duration: 25, repeat: Infinity, ease: 'easeInOut' }}
            />
            {/* Orange ember spots */}
            <motion.div
              className="absolute"
              style={{
                width: '100%', height: '100%',
                background: 'radial-gradient(ellipse at 50% 90%, rgba(255,140,0,0.05) 0%, transparent 30%)',
              }}
              animate={{ opacity: [0.3, 0.7, 0.3] }}
              transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
            />
          </div>

          {/* ═══ DRIFTING FOG LAYERS (skip on mobile for perf) ═══ */}
          {!isMobile && [0, 1, 2].map(i => (
            <motion.div
              key={`drift-${i}`}
              className="absolute pointer-events-none"
              style={{
                width: `${600 + i * 150}px`,
                height: `${150 + i * 40}px`,
                top: `${20 + i * 25}%`,
                left: '-40%',
                background: i === 0
                  ? 'radial-gradient(ellipse, rgba(168,85,247,0.06) 0%, transparent 70%)'
                  : i === 1
                  ? 'radial-gradient(ellipse, rgba(0,255,136,0.04) 0%, transparent 70%)'
                  : 'radial-gradient(ellipse, rgba(255,140,0,0.035) 0%, transparent 70%)',
                filter: 'blur(30px)',
              }}
              animate={{ x: ['0vw', '180vw'] }}
              transition={{ duration: 30 + i * 10, delay: i * 4, repeat: Infinity, ease: 'linear' }}
            />
          ))}

          {/* ═══ ARCANE CIRCLE — draws itself ═══ */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            {/* Outer ring — slow rotate */}
            <motion.div
              className="absolute rounded-full"
              style={{
                width: 420, height: 420,
                border: '1px solid rgba(168,85,247,0.12)',
                boxShadow: '0 0 40px rgba(168,85,247,0.06), inset 0 0 30px rgba(168,85,247,0.03)',
              }}
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1, rotate: [0, 360] }}
              transition={{ scale: { duration: 1.5, ease: 'easeOut' }, opacity: { duration: 1.5 }, rotate: { duration: 60, repeat: Infinity, ease: 'linear' } }}
            >
              {/* Tick marks on outer ring */}
              {[...Array(36)].map((_, i) => (
                <div key={i} className="absolute" style={{
                  width: '1px', height: i % 9 === 0 ? '10px' : '4px',
                  background: i % 9 === 0 ? 'rgba(168,85,247,0.3)' : 'rgba(168,85,247,0.1)',
                  left: '50%', top: '0',
                  transformOrigin: '50% 210px',
                  transform: `translateX(-50%) rotate(${i * 10}deg)`,
                }} />
              ))}
            </motion.div>

            {/* Middle ring — counter rotate */}
            <motion.div
              className="absolute rounded-full"
              style={{
                width: 320, height: 320,
                border: '1px solid rgba(0,255,136,0.08)',
                boxShadow: '0 0 25px rgba(0,255,136,0.04), inset 0 0 25px rgba(0,255,136,0.02)',
              }}
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1, rotate: [0, -360] }}
              transition={{ scale: { duration: 1.8, delay: 0.3, ease: 'easeOut' }, opacity: { duration: 1.8, delay: 0.3 }, rotate: { duration: 45, repeat: Infinity, ease: 'linear' } }}
            />

            {/* Inner glow ring (skip on mobile) */}
            {!isMobile && (
              <motion.div
                className="absolute rounded-full"
                style={{
                  width: 240, height: 240,
                  border: '1px solid rgba(255,140,0,0.06)',
                  boxShadow: '0 0 20px rgba(255,140,0,0.04)',
                }}
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: [0, 0.5, 0.3], rotate: [0, 360] }}
                transition={{ scale: { duration: 2, delay: 0.6, ease: 'easeOut' }, opacity: { duration: 4, repeat: Infinity }, rotate: { duration: 35, repeat: Infinity, ease: 'linear' } }}
              />
            )}

            {/* Core energy pulse (skip on mobile) */}
            {!isMobile && (
              <motion.div
                className="absolute rounded-full"
                style={{
                  width: 160, height: 160,
                  background: 'radial-gradient(circle, rgba(168,85,247,0.12) 0%, rgba(0,255,136,0.04) 40%, rgba(255,140,0,0.02) 60%, transparent 70%)',
                  filter: 'blur(20px)',
                }}
                animate={{ scale: [0.8, 1.3, 0.8], opacity: [0.3, 0.7, 0.3] }}
                transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
              />
            )}
          </div>

          {/* ═══ ORBITING GLYPHS ═══ */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            {orbitGlyphs.map((g, i) => (
              <motion.div
                key={`glyph-${i}`}
                className="absolute font-bold select-none"
                style={{
                  fontFamily: "'Cinzel', serif",
                  fontSize: g.size,
                  color: g.color,
                  textShadow: `0 0 8px ${g.glow}`,
                }}
                animate={{
                  rotate: [g.angle, g.angle + 360],
                }}
                transition={{ duration: g.duration, repeat: Infinity, ease: 'linear' }}
              >
                <div style={{ transform: `translateY(-${g.radius}px) rotate(-${g.angle}deg)` }}>
                  <motion.span animate={{ opacity: [0.3, 0.8, 0.3] }} transition={{ duration: 3 + i * 0.2, repeat: Infinity }}>
                    {g.glyph}
                  </motion.span>
                </div>
              </motion.div>
            ))}
          </div>

          {/* ═══ RISING EMBERS (tri-color) ═══ */}
          {embers.map(e => (
            <motion.div
              key={`ember-${e.id}`}
              className="absolute rounded-full pointer-events-none"
              style={{
                left: e.left, bottom: '-2%',
                width: e.w, height: e.h,
                background: e.bg,
                boxShadow: e.shadow,
              }}
              animate={{
                y: [0, e.yEnd],
                x: [e.xDrift],
                opacity: [0, 0.8, 0.3, 0],
              }}
              transition={{ duration: e.dur, delay: e.delay, repeat: Infinity, ease: 'easeOut' }}
            />
          ))}

          {/* ═══ CRT SCANLINE ═══ */}
          <div className="absolute inset-0 pointer-events-none z-[80]" style={{
            background: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,0.12) 2px, rgba(0,0,0,0.12) 4px)',
            mixBlendMode: 'overlay', opacity: 0.3,
          }} />

          {/* ═══ VIGNETTE ═══ */}
          <div className="absolute inset-0 pointer-events-none z-[70]"
            style={{ background: 'radial-gradient(ellipse at center, transparent 30%, rgba(2,0,4,0.6) 70%, rgba(2,0,4,0.95) 100%)' }} />

          {/* ═══ CENTER CONTENT ═══ */}
          <div className="relative z-[90] flex flex-col items-center" style={{ maxWidth: 500, padding: '0 24px' }}>

            {/* Logo */}
            <motion.div
              className="relative mb-8"
              animate={{ y: [0, -8, 0] }}
              transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
            >
              <img
                src={logoUrl}
                alt="Gas-Free Gremlins"
                data-testid="loading-logo"
                className="relative z-10"
                style={{
                  width: isMobile ? 120 : 160, height: isMobile ? 120 : 160, objectFit: 'contain',
                  filter: isMobile
                    ? 'drop-shadow(0 0 16px rgba(168,85,247,0.5))'
                    : 'drop-shadow(0 0 30px rgba(168,85,247,0.7)) drop-shadow(0 0 60px rgba(168,85,247,0.3)) drop-shadow(0 0 15px rgba(0,255,136,0.4))',
                }}
              />
            </motion.div>

            {/* Title */}
            <motion.div
              className="text-center mb-2"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.8 }}
            >
              <h1 className="text-3xl md:text-4xl font-bold uppercase tracking-[0.08em]"
                style={{ fontFamily: "'Cinzel Decorative', 'Cinzel', serif" }}>
                <span style={{
                  background: 'linear-gradient(90deg, #39ff14 0%, #7fff00 20%, #ffd700 40%, #ffaa00 55%, #e040fb 75%, #c026d3 90%, #9333ea 100%)',
                  WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                  filter: 'drop-shadow(0 0 15px rgba(57,255,20,0.4)) drop-shadow(0 0 25px rgba(224,64,251,0.3))',
                }}>Gas-Free Gremlins</span>
              </h1>
            </motion.div>

            {/* Subtitle whisper */}
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: [0, 0.5, 0.3] }}
              transition={{ delay: 1, duration: 3, repeat: Infinity, repeatType: 'reverse' }}
              className="text-[10px] tracking-[0.4em] uppercase mb-5"
              style={{ fontFamily: "'Cinzel', serif", color: 'rgba(168,85,247,0.5)', textShadow: '0 0 8px rgba(168,85,247,0.3)' }}
            >
              Born from chaos
            </motion.p>

            {/* Event Banner */}
            <AnimatePresence>
              {activeEvent && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  className="flex items-center gap-2.5 px-5 py-2 mb-5"
                  data-testid="event-banner"
                  style={{ border: `1px solid ${activeEvent.color}30`, background: `${activeEvent.color}08` }}
                >
                  <motion.div
                    className="w-1.5 h-1.5 rounded-full"
                    style={{ background: activeEvent.color, boxShadow: `0 0 8px ${activeEvent.color}` }}
                    animate={{ opacity: [0.5, 1, 0.5] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                  />
                  <span className="text-[10px] tracking-[0.2em] uppercase font-bold" style={{ color: activeEvent.color }}>{activeEvent.name}</span>
                  <span className="text-[8px] tracking-widest uppercase" style={{ color: 'rgba(255,255,255,0.2)' }}>// active</span>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Lore text — typewriter whisper */}
            <div className="h-6 mb-5 overflow-hidden">
              <AnimatePresence mode="wait">
                <motion.p
                  key={msgIdx}
                  initial={{ opacity: 0, y: 10, filter: 'blur(4px)' }}
                  animate={{ opacity: 0.6, y: 0, filter: 'blur(0px)' }}
                  exit={{ opacity: 0, y: -10, filter: 'blur(4px)' }}
                  transition={{ duration: 0.4 }}
                  className="text-sm text-center italic"
                  style={{ fontFamily: "'Courier New', monospace", color: 'rgba(0,255,136,0.5)', textShadow: '0 0 10px rgba(0,255,136,0.3)' }}
                >
                  {loreLines[msgIdx]}
                </motion.p>
              </AnimatePresence>
            </div>

            {/* ═══ MOLTEN CRACK PROGRESS BAR ═══ */}
            <div className="w-full max-w-[300px] relative mb-5">
              {/* Track */}
              <div className="h-[3px] w-full rounded-full" style={{ background: 'rgba(255,255,255,0.04)' }} />
              {/* Fill — molten gradient */}
              <motion.div
                className="absolute top-0 left-0 h-[3px] rounded-full"
                style={{
                  background: 'linear-gradient(90deg, #a855f7, #00ff88, #ff8c00)',
                  boxShadow: '0 0 10px rgba(168,85,247,0.5), 0 0 20px rgba(0,255,136,0.3), 0 0 30px rgba(255,140,0,0.2)',
                }}
                animate={{ width: `${Math.min(progress, 100)}%` }}
                transition={{ duration: 0.3 }}
              />
              {/* Glow at the tip */}
              <motion.div
                className="absolute top-[-4px] h-[11px] w-[11px] rounded-full"
                style={{
                  background: 'radial-gradient(circle, rgba(255,255,255,0.9), rgba(0,255,136,0.6), transparent)',
                  boxShadow: '0 0 12px rgba(0,255,136,0.8), 0 0 25px rgba(168,85,247,0.5)',
                  display: progress > 2 && progress < 100 ? 'block' : 'none',
                }}
                animate={{ left: `calc(${Math.min(progress, 100)}% - 5px)` }}
                transition={{ duration: 0.3 }}
              />
            </div>

            {/* Status / Enter */}
            {!isReady ? (
              <div className="flex items-center gap-2">
                <motion.div className="w-1 h-1 rounded-full" style={{ background: '#a855f7' }}
                  animate={{ opacity: [0.3, 1, 0.3] }} transition={{ duration: 1, repeat: Infinity }} />
                <span className="text-[11px] tracking-[0.3em] uppercase" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(168,85,247,0.4)' }}>
                  {Math.min(Math.round(progress), 100)}%
                </span>
              </div>
            ) : (
              <motion.button
                data-testid="enter-sewers-button"
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                onClick={handleEnter}
                className="group relative overflow-hidden px-12 py-4 cursor-pointer"
                style={{
                  background: 'rgba(0,0,0,0.4)',
                  border: '1px solid rgba(168,85,247,0.35)',
                  boxShadow: '0 0 20px rgba(168,85,247,0.15), 0 0 40px rgba(0,255,136,0.08)',
                }}
                whileHover={{
                  scale: 1.03,
                  borderColor: 'rgba(168,85,247,0.6)',
                  boxShadow: '0 0 30px rgba(168,85,247,0.3), 0 0 60px rgba(0,255,136,0.15), inset 0 0 20px rgba(168,85,247,0.08)',
                }}
                whileTap={{ scale: 0.97 }}
              >
                {/* Shimmer sweep */}
                <motion.div className="absolute inset-0 pointer-events-none"
                  style={{ background: 'linear-gradient(90deg, transparent, rgba(168,85,247,0.08), rgba(0,255,136,0.04), transparent)' }}
                  animate={{ x: ['-100%', '200%'] }}
                  transition={{ duration: 3, repeat: Infinity, ease: 'linear' }} />
                <span className="relative z-10 text-sm font-bold tracking-[0.2em] uppercase" style={{
                  fontFamily: "'Cinzel', serif",
                  background: 'linear-gradient(90deg, #c084fc, #00ff88)',
                  WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                }}>
                  Enter the Sewers
                </span>
              </motion.button>
            )}
          </div>

          {/* ═══ CORNER BRACKETS ═══ */}
          {[
            { pos: { top: 16, left: 16 }, borders: 'borderTop borderLeft', color: 'rgba(168,85,247,0.3)' },
            { pos: { top: 16, right: 16 }, borders: 'borderTop borderRight', color: 'rgba(0,255,136,0.25)' },
            { pos: { bottom: 16, left: 16 }, borders: 'borderBottom borderLeft', color: 'rgba(255,140,0,0.2)' },
            { pos: { bottom: 16, right: 16 }, borders: 'borderBottom borderRight', color: 'rgba(168,85,247,0.3)' },
          ].map((c, i) => (
            <motion.div
              key={i}
              className="absolute w-8 h-8 pointer-events-none z-[90]"
              style={{
                ...c.pos,
                borderTop: c.borders.includes('borderTop') ? `2px solid ${c.color}` : 'none',
                borderBottom: c.borders.includes('borderBottom') ? `2px solid ${c.color}` : 'none',
                borderLeft: c.borders.includes('borderLeft') ? `2px solid ${c.color}` : 'none',
                borderRight: c.borders.includes('borderRight') ? `2px solid ${c.color}` : 'none',
              }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.6 }}
              transition={{ delay: 0.3 + i * 0.15 }}
            />
          ))}

          {/* ═══ BOTTOM PROTOCOL TEXT ═══ */}
          <motion.div
            className="absolute bottom-5 left-1/2 -translate-x-1/2 flex items-center gap-2 z-[90]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 2 }}
          >
            <motion.div className="w-1 h-1 rounded-full" style={{ background: '#00ff88', boxShadow: '0 0 4px #00ff88' }}
              animate={{ opacity: [0.3, 0.8, 0.3] }} transition={{ duration: 2, repeat: Infinity }} />
            <span className="text-[9px] tracking-[0.4em] uppercase" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(0,255,136,0.25)' }}>
              Sewer Protocol v1.0
            </span>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default LoadingScreen;
